import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem, rgba } from 'polished';
import { Flexbox, FlexCol } from '../../Defaults/Flexbox/Flexbox';
import { __, __r } from 'react-i18n';
import { SanitasButtonPrimary } from '../../..';
import { Strong } from '../../Defaults/Defaults';
import { TextP } from '../../Defaults/Text';
import { ImageGalleryThumbnails } from '../ProductDetail/ImageGalleryThumbnails';

interface Props {
  description: string;
  descriptionBilling: string;
  addressBilling: string;
  address: string;
  openeningHours: string;
  phone: string;
  email: string;
  mobilePhone: string;
  ico: string;
  dic: string;
  icDPH: string;
  mapSrc: string;
  images: any[];
  openModal: (index) => void;
}

export function ContactSection({
  description,
  descriptionBilling,
  addressBilling,
  address,
  openeningHours,
  phone,
  email,
  mobilePhone,
  ico,
  dic,
  icDPH,
  mapSrc,
  images,
  openModal,
}: Props) {
  const [activeId, setActiveId] = React.useState(0);

  const renderFirstPart = visible => {
    return (
      <Wrapper visible={visible}>
        <DescriptionText bottomOffset={32}>{`${description}`}</DescriptionText>
        <StrongP bottomOffset={8}>{`${address}`}</StrongP>
        <SectionText bottomOffset={24}>{`${openeningHours}`}</SectionText>
        <SectionTextCover>
          <StrongP>{__('E-mail')}</StrongP>
          <EmailPhoneText href={`mailto:${email}`}>{email}</EmailPhoneText>
        </SectionTextCover>

        <SectionTextCover>
          <StrongP>{__('Telefón:')}</StrongP>
          <EmailPhoneText href={`tel:${phone}`}>{phone}</EmailPhoneText>
        </SectionTextCover>

        <SectionTextCover>
          <StrongP>{__('Mobilný telefón:')}</StrongP>
          <EmailPhoneText href={`tel:${mobilePhone}`}>
            {mobilePhone}
          </EmailPhoneText>
        </SectionTextCover>
        <ImageGalleryThumbnails
          images={images}
          openModal={openModal}
          isHomepage={true}
          marginRight={8}
        />
      </Wrapper>
    );
  };

  const renderSecondPart = visible => {
    return (
      <Wrapper visible={visible}>
        <DescriptionText
          bottomOffset={32}
        >{`${descriptionBilling}`}</DescriptionText>
        <StrongP bottomOffset={8}>{`${addressBilling}`}</StrongP>
        <SectionTextCover>
          <StrongP>{__('IČO')}</StrongP>
          <EmailPhoneText>{ico}</EmailPhoneText>
        </SectionTextCover>

        <SectionTextCover>
          <StrongP>{__('DIČ:')}</StrongP>
          <EmailPhoneText>{dic}</EmailPhoneText>
        </SectionTextCover>

        <SectionTextCover>
          <StrongP>{__('IČ DPH:')}</StrongP>
          <EmailPhoneText>{icDPH}</EmailPhoneText>
        </SectionTextCover>
      </Wrapper>
    );
  };

  return (
    <ContactSectionCover>
      <InfoSection>
        <SectionTitle>{__('Kde nás nájdete?')}</SectionTitle>
        <ButtonsCover>
          <ContactPrimaryButton
            // tslint:disable-next-line:jsx-no-lambda
            onClick={() => setActiveId(0)}
            active={activeId === 0}
          >
            Prevádzka
          </ContactPrimaryButton>
          <ContactSecondaryButton
            // tslint:disable-next-line:jsx-no-lambda
            onClick={() => setActiveId(1)}
            active={activeId === 1}
          >
            Fakturačné údaje
          </ContactSecondaryButton>
        </ButtonsCover>
        {renderFirstPart(activeId === 0)}
        {renderSecondPart(activeId === 1)}
      </InfoSection>
      <MapSection>
        <img src={mapSrc} alt="map" />
      </MapSection>
    </ContactSectionCover>
  );
}

const Wrapper = styled.div<{ visible: boolean }>`
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  height: ${({ visible }) => (visible ? 'auto' : 0)};
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

const ContactSectionCover = styled(Flexbox)`
  padding-top: ${rem(56)};
  padding-bottom: ${rem(56)};
  justify-content: space-between;
  font-family: ${({ theme }) => theme.font.primary};

  ${({ theme }) => theme.media(0, 'sm')} {
    display: block;
    padding-top: ${rem(48)};
    padding-bottom: ${rem(48)};
  }
`;

const InfoSection = styled.div`
  max-width: ${rem(312)};
  margin-right: ${rem(16)};
`;

const MapSection = styled.div`
  img {
    max-height: ${rem(426)};
  }

  ${({ theme }) => theme.mediab.l1050`
    margin: auto ${rem(4)};
  `}
`;

const SectionTitle = styled.h2`
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(30)};
  line-height: 1.2666666667;
  margin: 0;
  margin-bottom: ${rem(32)};

  ${({ theme }) => theme.media(0, 'sm')} {
    font-size: ${rem(24)};
    line-height: ${rem(28)};
  }
`;

const ButtonsCover = styled.div`
  display: flex;
  margin-bottom: ${rem(32)};

  ${({ theme }) => theme.mediab.s450`
    flex-direction: column;
  `}
`;

const ContactPrimaryButton = styled(SanitasButtonPrimary)<{ active: boolean }>`
  background-color: ${({ active }) =>
    active ? '#3962A8' : 'rgba(49, 39, 131, 0.04)'};
  color: ${({ active }) =>
    active ? `${({ theme }) => theme.colors.white}` : '#3962A8'};
  display: inline-block;
  margin-right: ${rem(2)};
  padding-left: ${rem(24)};
  padding-right: ${rem(24)};

  ${({ theme }) => theme.media(0, 'sm')} {
    width: initial;
  }

  &:hover {
    color: brightness(90%);
  }

  ${({ theme }) => theme.mediab.s450`
    margin: 0;
  `}
`;

const ContactSecondaryButton = styled(SanitasButtonPrimary)<{
  active: boolean;
}>`
  background-color: ${({ active }) =>
    active ? '#3962A8' : 'rgba(49, 39, 131, 0.04)'};
  color: ${({ active }) =>
    active ? `${({ theme }) => theme.colors.white}` : '#3962A8'};
  display: inline-block;
  padding-left: ${rem(24)};
  padding-right: ${rem(24)};
  white-space: nowrap;

  ${({ theme }) => theme.media(0, 'sm')} {
    width: initial;
  }
`;

const SectionText = styled(TextP)<{ bottomOffset?: number }>`
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  font-weight: 400;
  margin-bottom: ${props =>
    props.bottomOffset ? `${props.bottomOffset}px` : '0px'};
`;

const StrongP = styled(SectionText)`
  font-weight: 700;
`;

const DescriptionText = styled(StrongP)`
  ${({ theme }) => theme.media(0, 'sm')} {
    margin-bottom: ${rem(24)};
  }
`;

const EmailPhoneText = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  font-weight: 400;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionTextCover = styled.div`
  display: block;
  margin-bottom: ${rem(8)};
  p {
    display: inline-block;
    width: ${rem(126)};
  }
`;
